import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Amplify, API, graphqlOperation } from 'aws-amplify';
import { Alert } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import awsExports from '../aws-exports';

// Configuration for the tenant management API
const TENANT_API_CONFIG = {
  url: 'https://giecsg3f4nc4zjfbwzdkf7z4em.appsync-api.us-east-1.amazonaws.com/graphql',
  region: 'us-east-1',
  apiKey: 'da2-mrx526ilibdenbt3avuvtjgtqm'
};

/**
 * Helper to determine if we're running in local development
 * @returns {boolean} True if running on localhost
 */
const isLocalDevelopment = () => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname.endsWith('.localhost');
};

/**
 * Get the appropriate redirect URL based on environment and app configuration
 * @param {string} tenantId - The current tenant ID
 * @param {Object} saasApplication - The SaaS application configuration
 * @returns {string} The redirect URL to use
 */
const getRedirectUrl = (tenantId, saasApplication) => {
  if (isLocalDevelopment()) {
    // Get the actual port from window.location for local development
    const port = window.location.port || '3000';
    // Add debug log to track which port is being used
    return `http://localhost:${port}${saasApplication.redirectSignInPath}`;
  }

  // Replace {tenant} placeholder with actual tenant ID if present
  const domain = saasApplication.appDomain.startsWith('{tenant}')
    ? saasApplication.appDomain.replace('{tenant}', tenantId)
    : saasApplication.appDomain;

  return `https://${domain}${saasApplication.redirectSignInPath}`;
};

// GraphQL query to fetch tenant configuration
const getTenantUserPool = /* GraphQL */ `
  query GetTenantConfiguration($tenantId: String!, $appName: String!) {
    listCognitoTenantUserPoolAppClients(
      filter: { and: { saasApplicationId: { eq: $appName }, tenantUserPoolId: { eq: $tenantId } } }
    ) {
      items {
        clientId
        tenantUserPool {
          hostedUiUrl
          userPoolId
          isActive
        }
        saasApplication {
          appDomain
          redirectSignInPath
          redirectSignOutPath
          oauthScopes
          identityPoolId
        }
      }
    }
  }
`;

const TenantConfigurationContext = createContext();

/**
 * Custom hook to use tenant configuration
 * @returns {Object} The tenant configuration context value
 * @throws {Error} If used outside of TenantConfigurationProvider
 */
export function useTenantConfiguration() {
  const context = useContext(TenantConfigurationContext);
  if (!context) {
    throw new Error('useTenantConfiguration must be used within a TenantConfigurationProvider');
  }
  return context;
}

/**
 * Provider component that fetches and manages tenant-specific configuration
 * @param {Object} props - Component props
 * @param {string} props.tenantId - The ID of the tenant to fetch configuration for
 * @param {React.ReactNode} props.children - Child components
 */
export function TenantConfigurationProvider({ children, tenantId }) {
  const [state, setState] = useState({
    config: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    async function loadTenantConfig() {
      try {
        const response = await fetch(TENANT_API_CONFIG.url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': TENANT_API_CONFIG.apiKey
          },
          body: JSON.stringify({
            query: getTenantUserPool,
            variables: { 
              tenantId,
              appName: 'gridfin'
            }
          })
        });

        const result = await response.json();

        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        // Find the app client configuration from the response
        const appClient = result.data.listCognitoTenantUserPoolAppClients.items[0];
        if (!appClient) {
          throw new Error('App client configuration not found');
        }

        if (!appClient.tenantUserPool) {
          throw new Error('Tenant user pool configuration not found');
        }

        if (!appClient.saasApplication) {
          throw new Error('SaaS application configuration not found');
        }

        const { tenantUserPool, saasApplication } = appClient;

        // Verify tenant is active
        if (!tenantUserPool.isActive) {
          throw new Error('Tenant is not active');
        }

        const redirectUrl = getRedirectUrl(tenantId, saasApplication);

        // Create modified Amplify configuration with tenant-specific settings
        const modifiedAwsExports = {
          ...awsExports,
          aws_user_pools_id: tenantUserPool.userPoolId,
          aws_user_pools_web_client_id: appClient.clientId,
          aws_cognito_identity_pool_id: saasApplication.identityPoolId,
          oauth: {
            domain: new URL(tenantUserPool.hostedUiUrl).hostname,
            scope: saasApplication.oauthScopes.split(','),
            redirectSignIn: redirectUrl,
            redirectSignOut: redirectUrl,
            responseType: 'code',
          },
        };

        // Configure Amplify with tenant-specific configuration
        Amplify.configure(modifiedAwsExports);

        setState({
          config: {
            ...tenantUserPool,
            identityPoolId: saasApplication.identityPoolId,
            appDomain: saasApplication.appDomain,
          },
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error('Error loading tenant configuration:', error);
        setState({
          config: null,
          isLoading: false,
          error: error instanceof Error ? error : new Error('Failed to load tenant configuration'),
        });
      }
    }

    loadTenantConfig();
  }, [tenantId]);

  if (state.isLoading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight={200}
      >
        <CircularProgress />
        <Typography variant="body1" style={{ marginTop: 16 }}>
          Loading tenant configuration...
        </Typography>
      </Box>
    );
  }

  if (state.error) {
    return (
      <Alert 
        severity="error" 
        style={{ marginBottom: 16 }}
      >
        <Typography variant="h6">Configuration Error</Typography>
        <Typography>{state.error.message}</Typography>
      </Alert>
    );
  }

  return (
    <TenantConfigurationContext.Provider value={state}>
      {children}
    </TenantConfigurationContext.Provider>
  );
}

// PropTypes validation
TenantConfigurationProvider.propTypes = {
  children: PropTypes.node.isRequired,
  tenantId: PropTypes.string.isRequired,
};

export default TenantConfigurationProvider; 